import React, { useState } from 'react';
import { TextField, MenuItem, Select, FormControl, InputLabel, Checkbox, ListItemText, OutlinedInput, Button, Box, SelectChangeEvent, Grid, Autocomplete, Alert, FormHelperText } from '@mui/material';
import { User } from '../types/userTypes';
import { validateRUT, validateDNI, formatRUT } from '../utils/validation';
import { Positions } from '../types/EmployeeTypes';
import { rols } from '../types/rolsTypes';
import { ADMIN_SISTEMA } from '../contexts/constants/profiles';

interface UserFormProps {
    onAddUser: (user: Omit<User, 'id'>) => void;
    projects: { id: number; name: string }[];
    positions: Positions[];
    rols: rols[];
    countries?: { id: number; name: string }[];
}

const UserForm: React.FC<UserFormProps> = ({ onAddUser, projects, positions, rols, countries = [] }) => {
    const [formData, setFormData] = useState<Omit<User, 'id'>>({
        identificationNum: '',
        name: '',
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        position: '',
        profile: 1,
        projects: [],
        countries: [],
        description: '',
        passwordActive: 1,
        docType: 1,
        active: 1,
        rols: { id: 1, description: '' }
    });

    const [errorMessageDoc, setErrorMessageDoc] = useState<string>('');
    const [errorMessage, setErrorMessage] = useState<string>('');
    const docTypeValue = formData.docType === 1 ? 'RUT' : 'DNI';
    const [emailError, setEmailError] = useState<string>('');
    const [passwordError, setPasswordError] = useState<string>('');
    const [positionError, setPositionError] = useState<string>('');
    const [countryError, setCountryError] = useState<string>('');
    const [isFormValid, setIsFormValid] = useState<boolean>(false);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        let updatedFormData = { ...formData };

        // Limpieza de mensajes de error específicos al comenzar a escribir
        if (name === 'email') setEmailError('');
        if (name === 'password') setPasswordError('');
        if (name === 'position') setPositionError('');

        // Validación del número de identificación (RUT/DNI)
        if (name === 'dni') {
            const formattedValue = docTypeValue === 'RUT' ? formatRUT(value) : value;
            updatedFormData = { ...updatedFormData, identificationNum: formattedValue };

            if (docTypeValue === 'RUT' && !validateRUT(formattedValue)) {
                setErrorMessageDoc('RUT no válido');
            } else if (docTypeValue === 'DNI' && !validateDNI(value)) {
                setErrorMessageDoc('DNI no válido');
            } else {
                setErrorMessageDoc('');
            }
        }
        // Validación del correo electrónico
        else if (name === 'email') {
            if (!validateEmail(value)) {
                setEmailError('Correo electrónico inválido');
            } else {
                setEmailError('');
            }
            updatedFormData = { ...updatedFormData, email: value };
        }
        // Validación de la contraseña
        else if (name === 'password') {
            if (value.trim() === '') {
                setPasswordError('La contraseña es obligatoria');
            } else {
                setPasswordError('');
            }
            updatedFormData = { ...updatedFormData, password: value };
        }
        // Validación del cargo (position)
        else if (name === 'position') {
            if (value.trim() === '') {
                setPositionError('El cargo es obligatorio');
            } else {
                setPositionError('');
            }
            updatedFormData = { ...updatedFormData, position: value };
        }
        // Manejo general para otros campos
        else {
            updatedFormData = { ...updatedFormData, [name]: value };
        }

        // Actualización del estado del formulario
        setFormData(updatedFormData);
        validateForm(updatedFormData);
    };


    const validateForm = (data: Omit<User, 'id'>) => {
        const isValid =
            validateEmail(data.email || '')
        setIsFormValid(isValid);
    };


    const handleProfileChange = (event: SelectChangeEvent<number>) => {
        const selectedProfileId = event.target.value as number;
        const selectedProfile = rols.find(profile => profile.id === selectedProfileId);

        if (selectedProfile) {
            setFormData({
                ...formData,
                profile: selectedProfile.id,
                rols: {
                    id: selectedProfile.id,
                    description: selectedProfile.description,
                },
            });
        }
    };

    // Maneja la selección de proyectos
    const handleProjectsChange = (event: SelectChangeEvent<number[]>) => {
        const value = event.target.value as number[];
        setFormData({ ...formData, projects: value });
    };

    // Maneja la selección de países
    const handleCountriesChange = (event: SelectChangeEvent<number[]>) => {
        const value = event.target.value as number[];
        setFormData({ ...formData, countries: value });
        setCountryError('');
    };

    const handlePositionChange = (event: any, newValue: string | null | undefined) => {
        if (newValue) {
            setPositionError('');
        }
        setFormData({
            ...formData,
            position: newValue || ''
        });
    };

    // Maneja el cambio del tipo de documento
    const handleDocumentTypeChange = (e: SelectChangeEvent<string>) => {
        const value = e.target.value;
        const docTypeNumber = value === 'RUT' ? 1 : 2;
        setFormData({ ...formData, docType: docTypeNumber, identificationNum: '' });
        setErrorMessageDoc('');
    };


    const handleAddClick = async () => {
        // Validación previa
        if (!formData.email) {
            setEmailError('El correo electrónico es obligatorio');
            return;
        }

        if (!validateEmail(formData.email)) {
            setEmailError('Correo electrónico inválido');
            return;
        }

        if (!formData.password) {
            setPasswordError('La contraseña es obligatoria');
            return;
        }

        if (!formData.position) {
            setPositionError('El cargo es obligatorio');
            return;
        }

        if (docTypeValue === 'RUT' && !validateRUT(formData.identificationNum)) {
            setErrorMessageDoc('RUT no válido');
            return;
        }

        // Un usuario activo sin pais asignado no queda restringido: al no haber pais que enviar en la
        // cabecera los servicios dejan de acotar y veria la informacion de todos los paises. El
        // Administrador de Sistema se exceptua porque por diseño trabaja en todos.
        const perfilSeleccionado = rols.find(profile => profile.id === formData.profile)?.description;
        if (perfilSeleccionado !== ADMIN_SISTEMA && (formData.countries?.length ?? 0) === 0) {
            setCountryError('Asigna al menos un país: sin país el usuario vería la información de todos los países.');
            return;
        }

        try {
            const transformedData = {
                ...formData,
                docType: docTypeValue === 'RUT' ? 1 : 2,
                active: 1,
                activePassword: 1,
                profile: formData.profile,
            };

            await onAddUser(transformedData); // ahora se espera que lance error si ocurre

            // Limpiar formulario
            setFormData({
                identificationNum: '',
                name: '',
                firstName: '',
                lastName: '',
                email: '',
                password: '',
                position: '',
                profile: 1,
                projects: [],
                countries: [],
                description: '',
                docType: 1,
                rols: { id: 1, description: '' },
            });

            // Limpiar errores
            setErrorMessageDoc('');
            setEmailError('');
            setPasswordError('');
            setPositionError('');
            setErrorMessage('');
        } catch (error: any) {
            const message =
                error?.response?.data?.mensaje || 'Error al agregar el usuario. Inténtalo de nuevo.';

            setErrorMessage(message);
        }
    };


    const validateEmail = (email: string): boolean => {
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        return emailRegex.test(email);
    };

    return (
        <Box sx={{ p: 2, backgroundColor: '#f9f9f9', borderRadius: 2, border: '1px solid #ddd' }}>
            <Grid container spacing={2}>
                {/* Selector para elegir entre RUT o DNI */}
                <Grid item xs={12} md={1}>
                    <FormControl fullWidth variant="outlined">
                        <InputLabel>Tipo de Documento</InputLabel>
                        <Select
                            name='docType'
                            value={docTypeValue}
                            onChange={handleDocumentTypeChange}
                            label="Tipo de Documento"
                        >
                            <MenuItem value="RUT">RUT</MenuItem>
                            <MenuItem value="DNI">DNI</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item xs={12} md={2}>
                    <TextField
                        label={docTypeValue}
                        name="dni"
                        value={formData.identificationNum}
                        onChange={handleInputChange}
                        variant="outlined"
                        fullWidth
                        placeholder={docTypeValue === 'RUT' ? '12345678-9' : '12345678'}
                        error={Boolean(errorMessageDoc)}
                        helperText={errorMessageDoc}
                    />
                </Grid>

                <Grid item xs={12} md={3}>
                    <TextField label="Nombre" name="name" value={formData.name} onChange={handleInputChange} variant="outlined" fullWidth />
                </Grid>

                <Grid item xs={12} md={3}>
                    <TextField label="Apellido Paterno" name="firstName" value={formData.firstName} onChange={handleInputChange} variant="outlined" fullWidth />
                </Grid>
                <Grid item xs={12} md={3}>
                    <TextField label="Apellido Materno" name="lastName" value={formData.lastName} onChange={handleInputChange} variant="outlined" fullWidth />
                </Grid>
                <Grid item xs={12} md={3}>
                    <TextField
                        label="Email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        variant="outlined"
                        fullWidth
                        error={Boolean(emailError)}
                        helperText={emailError} // Muestra el mensaje de error si el email es inválido
                    />
                </Grid>
                <Grid item xs={12} md={3}>
                    <TextField label="Contraseña" name="password" value={formData.password} onChange={handleInputChange} variant="outlined" error={Boolean(passwordError)} helperText={passwordError} fullWidth />
                </Grid>
                {/*<Grid item xs={12} md={3}>
                    <FormControl fullWidth variant="outlined">
                        <InputLabel>Cargo</InputLabel>
                        <Select
                        value={formData.position} 
                        onChange={handlePositionChange} 
                        label="Cargo"
                        required
                        >
                        {positions.map((position) => (
                            <MenuItem key={position.description} value={position.description}>
                            {position.description} 
                            </MenuItem>
                        ))}
                        </Select>
                    </FormControl>
                </Grid>*/}

                <Grid item xs={12} md={3}>
                    <FormControl fullWidth variant="outlined">
                        <Autocomplete
                            value={formData.position}
                            onChange={handlePositionChange}
                            options={Array.from(new Set(positions.map((position) => position.description)))}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Cargo"
                                    variant="outlined"
                                    error={Boolean(positionError)}
                                    helperText={positionError}
                                    required
                                />
                            )}
                        />

                    </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                    <FormControl fullWidth variant="outlined">
                        <InputLabel>Perfil</InputLabel>
                        <Select
                            name="profile"
                            value={rols.some((profile) => profile.id === formData.profile) ? formData.profile : ''}
                            onChange={handleProfileChange}
                            label="Perfil"
                        >
                            {rols.map((profile) => (
                                <MenuItem key={profile.id} value={profile.id}>
                                    {profile.description}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                    <FormControl variant="outlined" fullWidth error={Boolean(countryError)}>
                        <InputLabel>Países</InputLabel>
                        <Select
                            name="countries"
                            multiple
                            value={formData.countries || []}
                            onChange={handleCountriesChange}
                            input={<OutlinedInput label="Países" />}
                            renderValue={(selected) => (selected as number[]).map(id => countries.find(c => c.id === id)?.name).join(', ')}
                        >
                            {countries.map((country) => (
                                <MenuItem key={country.id} value={country.id}>
                                    <Checkbox checked={Array.isArray(formData.countries) && formData.countries.indexOf(country.id) > -1} />
                                    <ListItemText primary={country.name} />
                                </MenuItem>
                            ))}
                        </Select>
                        {countryError && <FormHelperText>{countryError}</FormHelperText>}
                    </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                    <FormControl variant="outlined" fullWidth>
                        <InputLabel>Proyectos</InputLabel>
                        <Select
                            name="projects"
                            multiple
                            value={formData.projects}
                            onChange={handleProjectsChange}
                            input={<OutlinedInput label="Proyectos" />}
                            renderValue={(selected) => (selected as number[]).map(id => projects.find(p => p.id === id)?.name).join(', ')}
                        >
                            {projects.map((project) => (
                                <MenuItem key={project.id} value={project.id}>
                                    <Checkbox checked={Array.isArray(formData.projects) && formData.projects.indexOf(project.id) > -1} />
                                    <ListItemText primary={project.name} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item xs={12}>
                    <TextField label="Descripción" name="description" value={formData.description} onChange={handleInputChange} variant="outlined" fullWidth multiline minRows={4} />
                </Grid>

                <Grid item xs={12}>
                {errorMessage && (
                    <Box display= "flex" justifyContent= "center">
                        <Alert severity="error" onClose={() => setErrorMessage('')} >
                            {errorMessage}
                        </Alert>
                    </Box>
                    
                )}
                </Grid>
                
                <Grid item xs={12}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleAddClick}
                        fullWidth
                        disabled={!isFormValid}
                    >
                        Agregar
                    </Button>
                </Grid>
            </Grid>
        </Box>
    );
};

export default UserForm;
